import type { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import {
	createContext,
	forwardRef,
	useCallback,
	useContext,
	useEffect,
	useState,
	type HTMLAttributes,
} from "react";
import { ChevronIcon } from "../icons/chevron";
import { cn } from "../lib/utils";
import { Button, ButtonProps } from "./button";

type CarouselContextType = {
	emblaApi: ReturnType<typeof useEmblaCarousel>[1];
	emblaRef: ReturnType<typeof useEmblaCarousel>[0];
};

const CarouselContext = createContext<CarouselContextType | null>(null);

const useCarousel = () => {
	const context = useContext(CarouselContext);

	if (!context) {
		throw new Error("Carousel components must be used within a Carousel");
	}

	return context;
};

export const Carousel = forwardRef<
	HTMLDivElement,
	HTMLAttributes<HTMLDivElement> & { options?: EmblaOptionsType }
>(({ children, options, className, ...props }, ref) => {
	const [emblaRef, emblaApi] = useEmblaCarousel(options);

	return (
		<CarouselContext.Provider value={{ emblaApi, emblaRef }}>
			<div
				className={cn("relative", className)}
				ref={ref}
				data-id="carousel"
				role="region"
				aria-roledescription="carousel"
				{...props}
			>
				{children}
			</div>
		</CarouselContext.Provider>
	);
});
Carousel.displayName = "Carousel";

export const CarouselContent = forwardRef<
	HTMLDivElement,
	HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
	const { emblaRef } = useCarousel();

	return (
		<div
			ref={emblaRef}
			data-id="carousel-content"
			className="h-full overflow-hidden"
		>
			<div
				className={cn("flex h-full backface-hidden", className)}
				ref={ref}
				{...props}
			>
				{children}
			</div>
		</div>
	);
});
CarouselContent.displayName = "CarouselContent";

export const CarouselItem = forwardRef<
	HTMLDivElement,
	HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
	<div
		ref={ref}
		data-id="carousel-item"
		className={cn("relative h-full min-w-0 flex-[0_0_100%]", className)}
		{...props}
	>
		{children}
	</div>
));
CarouselItem.displayName = "CarouselItem";

export const CarouselPrevious = ({ className, ...props }: ButtonProps) => {
	const { emblaApi } = useCarousel();

	return (
		<Button
			variant={props.variant ?? "white"}
			size="icon"
			onClick={() => emblaApi?.scrollPrev()}
			className={cn(
				"left-bb absolute top-1/2 -translate-y-1/2 rounded-full focus:opacity-100",
				className,
			)}
			{...props}
		>
			<ChevronIcon className="size-6 rotate-90" />
			<span className="sr-only">Previous</span>
		</Button>
	);
};

export const CarouselNext = ({ className, ...props }: ButtonProps) => {
	const { emblaApi } = useCarousel();

	return (
		<Button
			variant={props.variant ?? "white"}
			size="icon"
			onClick={() => emblaApi?.scrollNext()}
			className={cn(
				"right-bb absolute top-1/2 -translate-y-1/2 rounded-full focus:opacity-100",
				className,
			)}
			{...props}
		>
			<ChevronIcon className="size-6 -rotate-90" />
			<span className="sr-only">Next</span>
		</Button>
	);
};

export const CarouselProgressIndicator = forwardRef<
	HTMLDivElement,
	HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
	const { emblaApi } = useCarousel();
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

	const onSelect = useCallback(() => {
		if (!emblaApi) return;
		setSelectedIndex(emblaApi.selectedScrollSnap());
	}, [emblaApi]);

	const onInit = useCallback(() => {
		if (!emblaApi) return;
		setScrollSnaps(emblaApi.scrollSnapList());
	}, [emblaApi]);

	useEffect(() => {
		if (!emblaApi) return;
		onSelect();
		onInit();
		emblaApi.on("select", onSelect);
		emblaApi.on("reInit", onInit);

		return () => {
			emblaApi.off("select", onSelect);
			emblaApi.off("reInit", onInit);
		};
	}, [emblaApi, onSelect, onInit]);

	return (
		<div
			className={cn(
				"bb-text-micro border-gray-border/40 text-gray-2 inline rounded-md border bg-white px-2 py-1 tabular-nums",
				className,
			)}
			ref={ref}
			{...props}
		>
			{selectedIndex + 1}/{scrollSnaps.length}
		</div>
	);
});

export const CarouselThumbnails = forwardRef<
	HTMLDivElement,
	HTMLAttributes<HTMLDivElement> & { thumbnails?: React.ReactNode[] }
>(({ className, thumbnails = [], ...props }, ref) => {
	const [currentIndex, setCurrentIndex] = useState(0);
	const { emblaApi } = useCarousel();
	const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
		containScroll: "keepSnaps",
		dragFree: true,
	});

	const onThumbClick = useCallback(
		(index: number) => {
			if (!emblaApi || !emblaThumbsApi) return;
			emblaApi.scrollTo(index);
		},
		[emblaApi, emblaThumbsApi],
	);

	const onSelect = useCallback(() => {
		if (!emblaApi || !emblaThumbsApi) return;
		const newIndex = emblaApi.selectedScrollSnap();
		emblaThumbsApi.scrollTo(newIndex);
		setCurrentIndex(newIndex);
	}, [emblaApi, emblaThumbsApi]);

	useEffect(() => {
		if (!emblaApi) return;
		onSelect();
		emblaApi.on("select", onSelect);
		emblaApi.on("reInit", onSelect);

		return () => {
			emblaApi.off("select", onSelect);
			emblaApi.off("reInit", onSelect);
		};
	}, [emblaApi, onSelect]);

	return (
		<div
			className={cn("mt-2", className)}
			ref={ref}
			{...props}
			data-id="thumbs"
		>
			<div className="overflow-hidden" ref={emblaThumbsRef}>
				<div className="-ml-2 flex flex-row max-md:items-center max-md:justify-center">
					{thumbnails.map((thumbnail, index) => (
						<div
							key={index}
							className={cn(
								"-mt-1 min-w-0 py-1 pl-2",
								"md:flex-[0_0_25%]", // 100% ÷ 4 = 25%
								"lg:flex-[0_0_20%]", // 100% ÷ 5 = 20%
								"xl:flex-[0_0_14.2857142857%]", // 100% ÷ 7 = 14,2857142857%
							)}
						>
							<button
								type="button"
								onClick={() => onThumbClick(index)}
								aria-label={`Go to slide ${index + 1}`}
								aria-current={currentIndex === index}
								className="tab-focus relative isolate rounded-xs md:overflow-hidden"
							>
								<div className="max-md:hidden">{thumbnail}</div>

								<div
									className={cn(
										"bg-gray-4/30 absolute inset-0 z-10 transition-opacity max-md:hidden",
										{
											"opacity-0": currentIndex === index,
											"opacity-100": currentIndex !== index,
										},
									)}
								/>

								<div
									className={cn("size-1 rounded-full md:hidden", {
										"bg-gray-2": true,
										"bg-gray-4 size-1.5 translate-y-0.25":
											index === currentIndex,
									})}
								/>
							</button>
						</div>
					))}
				</div>
			</div>
		</div>
	);
});
CarouselThumbnails.displayName = "CarouselThumbnails";
